/**
 * Lead data health calculation utilities
 *
 * Calculates a health score for leads based on data completeness and validity.
 * Used to show validation badges on cards and issues on detail pages.
 *
 * @example
 * ```ts
 * const health = calculateLeadHealth(lead);
 * // { phone: 'valid', email: 'warning', address: 'missing', overall: 53 }
 * ```
 */

import { validatePhoneNumber } from './phone';
import { validateEmail } from './email';
import { toMillisSafe } from '../utils/timestamp';
import { toDateSafe } from '../utils/timestamp';
import type { Timestamp } from 'firebase/firestore';

/**
 * Health status for a single field
 */
export type HealthStatus = 'valid' | 'warning' | 'invalid' | 'missing';

/**
 * Complete health score for a lead
 */
export interface LeadHealthScore {
  phone: HealthStatus;
  phoneMessage?: string;
  email: HealthStatus;
  emailMessage?: string;
  address: HealthStatus;
  addressMessage?: string;
  nextStep: HealthStatus;
  nextStepMessage?: string;
  contact: HealthStatus;
  contactMessage?: string;
  overall: number; // 0-100
  issueCount: number;
  warningCount: number;
}

/**
 * Issue type for the issues banner
 */
export interface LeadIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Input type for health calculation (subset of Lead)
 */
export interface LeadHealthInput {
  phone?: string;
  email?: string;
  primaryContactPhone?: string;
  primaryContactEmail?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  nextStep?: string;
  nextStepDue?: Timestamp;
  lastContactedAt?: Timestamp;
}

/**
 * Calculate the health score for a lead
 */
export function calculateLeadHealth(lead: LeadHealthInput): LeadHealthScore {
  let issueCount = 0;
  let warningCount = 0;

  // === Phone validation ===
  const phoneValue = lead.phone || lead.primaryContactPhone;
  let phoneStatus: HealthStatus = 'missing';
  let phoneMessage: string | undefined;

  if (phoneValue) {
    const validation = validatePhoneNumber(phoneValue);
    if (!validation.isValid) {
      phoneStatus = 'invalid';
      phoneMessage = validation.error;
      issueCount++;
    } else if (validation.warning) {
      phoneStatus = 'warning';
      phoneMessage = validation.warning;
      warningCount++;
    } else {
      phoneStatus = 'valid';
    }
  } else {
    phoneMessage = 'No phone number';
    warningCount++;
  }

  // === Email validation ===
  const emailValue = lead.email || lead.primaryContactEmail;
  let emailStatus: HealthStatus = 'missing';
  let emailMessage: string | undefined;

  if (emailValue) {
    const validation = validateEmail(emailValue);
    if (!validation.isValid) {
      emailStatus = 'invalid';
      emailMessage = validation.error;
      issueCount++;
    } else if (validation.suggestion) {
      emailStatus = 'warning';
      emailMessage = `Did you mean: ${validation.suggestion}?`;
      warningCount++;
    } else {
      emailStatus = 'valid';
    }
  } else {
    emailMessage = 'No email address';
    warningCount++;
  }

  // === Address validation ===
  let addressStatus: HealthStatus = 'missing';
  let addressMessage: string | undefined;

  if (lead.address) {
    const { street, city, state, zip } = lead.address;
    const hasStreet = !!street && street.trim() !== '';
    const hasCity = !!city && city.trim() !== '';
    const hasState = !!state && state.trim() !== '';
    const hasZip = !!zip && zip.trim() !== '';

    // Fallback: try to find ZIP in concatenated address fields
    let hasZipFallback = false;
    if (!hasZip) {
      const zipRegex = /\b\d{5}(-\d{4})?\b/;
      const rawAddress = [street, city, state].filter(Boolean).join(' ');
      hasZipFallback = zipRegex.test(rawAddress);
    }

    // Check for placeholder values
    const placeholders = ['tbd', 'n/a', 'unknown', 'same', '123 main'];
    const isPlaceholder = street && placeholders.some(p =>
      street.toLowerCase().includes(p)
    );

    if (isPlaceholder) {
      addressStatus = 'invalid';
      addressMessage = 'Placeholder address detected';
      issueCount++;
    } else if (hasStreet && hasCity && hasState && (hasZip || hasZipFallback)) {
      addressStatus = 'valid';
    } else if (hasStreet || hasCity) {
      addressStatus = 'warning';
      const missing = [];
      if (!hasStreet) missing.push('street');
      if (!hasCity) missing.push('city');
      if (!hasState) missing.push('state');
      if (!hasZip && !hasZipFallback) missing.push('ZIP');
      addressMessage = `Missing: ${missing.join(', ')}`;
      warningCount++;
    } else {
      addressMessage = 'No address';
    }
  } else {
    addressMessage = 'No address';
  }

  // === Next step validation ===
  let nextStepStatus: HealthStatus = 'missing';
  let nextStepMessage: string | undefined;

  if (lead.nextStep && lead.nextStep.trim() !== '') {
    if (lead.nextStepDue) {
      const dueDate = toDateSafe(lead.nextStepDue);
      if (dueDate) {
        const now = new Date();
        const daysDiff = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff < 0) {
          nextStepStatus = 'invalid'; // Overdue
          nextStepMessage = `Overdue by ${Math.abs(daysDiff)} day${Math.abs(daysDiff) > 1 ? 's' : ''}`;
          issueCount++;
        } else if (daysDiff === 0) {
          nextStepStatus = 'warning'; // Due today
          nextStepMessage = 'Due today';
          warningCount++;
        } else {
          nextStepStatus = 'valid';
        }
      }
    } else {
      nextStepStatus = 'warning'; // Has next step but no due date
      nextStepMessage = 'No due date set';
      warningCount++;
    }
  } else {
    nextStepMessage = 'No next step defined';
    warningCount++;
  }

  // === Contact recency validation ===
  let contactStatus: HealthStatus = 'missing';
  let contactMessage: string | undefined;

  const lastContactedMs = toMillisSafe(lead.lastContactedAt);
  if (lastContactedMs !== null) {
    const daysSince = Math.floor(
      (Date.now() - lastContactedMs) / (1000 * 60 * 60 * 24)
    );

    if (daysSince > 30) {
      contactStatus = 'invalid';
      contactMessage = `No contact in ${daysSince} days`;
      issueCount++;
    } else if (daysSince > 14) {
      contactStatus = 'warning';
      contactMessage = `Last contact ${daysSince} days ago`;
      warningCount++;
    } else {
      contactStatus = 'valid';
      contactMessage = daysSince === 0 ? 'Contacted today' : `${daysSince}d ago`;
    }
  } else {
    contactMessage = 'Never contacted';
    warningCount++;
  }

  // === Calculate overall score ===
  // Weight: phone (25), email (25), address (20), nextStep (15), contact (15)
  const statusScores: Record<HealthStatus, number> = {
    valid: 100,
    warning: 60,
    invalid: 20,
    missing: 0,
  };

  const overall = Math.round(
    statusScores[phoneStatus] * 0.25 +
    statusScores[emailStatus] * 0.25 +
    statusScores[addressStatus] * 0.20 +
    statusScores[nextStepStatus] * 0.15 +
    statusScores[contactStatus] * 0.15
  );

  return {
    phone: phoneStatus,
    phoneMessage,
    email: emailStatus,
    emailMessage,
    address: addressStatus,
    addressMessage,
    nextStep: nextStepStatus,
    nextStepMessage,
    contact: contactStatus,
    contactMessage,
    overall,
    issueCount,
    warningCount,
  };
}

/**
 * Get a list of issues for the issues banner
 */
export function getLeadIssues(lead: LeadHealthInput): LeadIssue[] {
  const health = calculateLeadHealth(lead);
  const issues: LeadIssue[] = [];

  // Phone issues
  if (health.phone === 'invalid') {
    issues.push({ field: 'phone', message: health.phoneMessage || 'Invalid phone', severity: 'error' });
  } else if (health.phone === 'missing') {
    issues.push({ field: 'phone', message: 'No phone number', severity: 'warning' });
  } else if (health.phone === 'warning' && health.phoneMessage) {
    issues.push({ field: 'phone', message: health.phoneMessage, severity: 'warning' });
  }

  // Email issues
  if (health.email === 'invalid') {
    issues.push({ field: 'email', message: health.emailMessage || 'Invalid email', severity: 'error' });
  } else if (health.email === 'warning' && health.emailMessage) {
    issues.push({ field: 'email', message: health.emailMessage, severity: 'warning' });
  } else if (health.email === 'missing') {
    issues.push({ field: 'email', message: 'No email address', severity: 'warning' });
  }

  // Address issues
  if (health.address === 'invalid') {
    issues.push({ field: 'address', message: health.addressMessage || 'Invalid address', severity: 'error' });
  } else if (health.address === 'warning' && health.addressMessage) {
    issues.push({ field: 'address', message: health.addressMessage, severity: 'warning' });
  } else if (health.address === 'missing') {
    issues.push({ field: 'address', message: 'No address', severity: 'warning' });
  }

  // Next step issues
  if (health.nextStep === 'invalid') {
    issues.push({ field: 'nextStep', message: health.nextStepMessage || 'Next step overdue', severity: 'error' });
  } else if (health.nextStep === 'warning' && health.nextStepMessage) {
    issues.push({ field: 'nextStep', message: health.nextStepMessage, severity: 'warning' });
  } else if (health.nextStep === 'missing') {
    issues.push({ field: 'nextStep', message: 'No next step defined', severity: 'warning' });
  }

  // Contact recency issues
  if (health.contact === 'invalid') {
    issues.push({ field: 'contact', message: health.contactMessage || 'Stale lead', severity: 'error' });
  } else if (health.contact === 'warning' && health.contactMessage) {
    issues.push({ field: 'contact', message: health.contactMessage, severity: 'warning' });
  } else if (health.contact === 'missing') {
    issues.push({ field: 'contact', message: 'Never contacted', severity: 'warning' });
  }

  return issues;
}

/**
 * Get a color class based on health status
 */
export function getHealthStatusColor(status: HealthStatus): string {
  switch (status) {
    case 'valid': return 'text-green-600';
    case 'warning': return 'text-amber-500';
    case 'invalid': return 'text-red-500';
    case 'missing': return 'text-slate-300';
  }
}

/**
 * Get an icon character based on health status
 */
export function getHealthStatusIcon(status: HealthStatus): string {
  switch (status) {
    case 'valid': return '✓';
    case 'warning': return '⚠';
    case 'invalid': return '✗';
    case 'missing': return '○';
  }
}
